import { cn } from "@/lib/utils";

export const CITY_DOTS: Record<string, { x: number; y: number }> = {
  London: { x: 63, y: 78 },
  Manchester: { x: 48, y: 47 },
  Bristol: { x: 42, y: 74 },
  Birmingham: { x: 51, y: 62 },
  Brighton: { x: 63, y: 87 },
  Leeds: { x: 54, y: 44 },
  Edinburgh: { x: 42, y: 20 },
  Glasgow: { x: 34, y: 21 },
  Cardiff: { x: 38, y: 76 },
  Liverpool: { x: 42, y: 48 },
  Newcastle: { x: 53, y: 33 },
  Sheffield: { x: 53, y: 50 },
  Nottingham: { x: 55, y: 56 },
  Norwich: { x: 76, y: 62 },
  Oxford: { x: 56, y: 73 },
  Cambridge: { x: 68, y: 68 },
  Leicester: { x: 55, y: 59 },
  Reading: { x: 58, y: 76 },
  Southampton: { x: 54, y: 84 },
  Exeter: { x: 36, y: 84 },
  Belfast: { x: 22, y: 40 },
};

/** Simplified UK silhouette. Illustrative only. */
export function UKMap({
  dots = [],
  meetingPoint,
  className,
}: {
  dots?: { name: string; count: number }[];
  meetingPoint?: string;
  className?: string;
}) {
  const known = dots.filter((d) => d.name && CITY_DOTS[d.name]);
  const mp = meetingPoint && CITY_DOTS[meetingPoint] ? CITY_DOTS[meetingPoint] : undefined;

  return (
    <svg viewBox="0 0 100 100" className={cn("w-full h-full", className)} aria-hidden="true">
      <path
        d="M40 8 L47 12 L44 18 L49 22 L45 27 L50 29 L47 33 L52 31 L56 36 L53 41 L57 45 L55 50 L60 52 L58 57 L64 59 L70 58 L78 60 L80 64 L74 66 L72 71 L68 70 L66 75 L70 79 L66 84 L60 89 L54 87 L50 89 L44 86 L38 87 L33 83 L36 79 L34 74 L39 72 L37 66 L41 62 L38 56 L42 52 L38 48 L41 43 L37 38 L40 32 L36 26 L39 20 L35 14 Z"
        fill="hsl(var(--muted))"
        stroke="hsl(var(--border))"
        strokeWidth="0.6"
      />
      <path
        d="M24 34 L29 36 L28 43 L23 46 L19 42 L20 36 Z"
        fill="hsl(var(--muted))"
        stroke="hsl(var(--border))"
        strokeWidth="0.6"
      />
      {mp &&
        known.map((d) => (
          <line
            key={`l-${d.name}`}
            x1={CITY_DOTS[d.name].x}
            y1={CITY_DOTS[d.name].y}
            x2={mp.x}
            y2={mp.y}
            stroke="hsl(var(--primary))"
            strokeWidth="0.35"
            strokeDasharray="1.4 1.4"
            opacity="0.5"
          />
        ))}
      {known.map((d) => (
        <g key={d.name}>
          <circle
            cx={CITY_DOTS[d.name].x}
            cy={CITY_DOTS[d.name].y}
            r={Math.min(3.4, 1.4 + d.count * 0.22)}
            fill="hsl(var(--primary))"
            opacity="0.85"
          />
        </g>
      ))}
      {mp && (
        <g>
          <circle cx={mp.x} cy={mp.y} r="5" fill="hsl(var(--accent))" opacity="0.25" />
          <circle cx={mp.x} cy={mp.y} r="2.4" fill="hsl(var(--accent))" />
        </g>
      )}
    </svg>
  );
}

export function Chip({
  active,
  children,
  onClick,
  className,
}: {
  active?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-4 py-2 rounded-full border text-sm transition-all",
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-card text-foreground border-border hover:border-primary/50",
        className
      )}
    >
      {children}
    </button>
  );
}

export function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">{children}</p>
  );
}

export const money = (n: number) => `£${Math.round(n).toLocaleString("en-GB")}`;
